/**
 * PHẦN 6 - XỬ LÝ VÀ HIỂN THỊ LỖI THÂN THIỆN (ERROR MAPPING)
 * Chuẩn hóa mọi lỗi kỹ thuật hoặc tiếng Anh thô thành thông điệp thân thiện tiếng Việt.
 * Ứng dụng tuyệt đối không hiển thị mã lỗi kỹ thuật hoặc tiếng Anh thô ra giao diện người dùng.
 */

export const FRIENDLY_ERROR_MAP = {
  QUOTA: '⚠️ "API Key cá nhân của bạn đã hết hạn mức sử dụng miễn phí hôm nay. Vui lòng thử lại vào thời gian khác."',
  OVERLOADED: '⏳ "Hệ thống AI của Google hiện đang quá tải. Bạn chờ một lát rồi thử lại hoặc đổi sang AI models khác nhé."',
  API_KEY_INVALID: '🔑 "Gemini API Key của bạn không hợp lệ hoặc đã bị thu hồi. Vui lòng vào Cài đặt để cập nhật lại API Key mới."',
  SAFETY: '🛡️ "Nội dung lệnh hoặc tài liệu đính kèm có chứa thông tin nhạy cảm vi phạm tiêu chuẩn an toàn của AI. Bạn vui lòng điều chỉnh lại nhé."',
  TIMEOUT: '📶 "Mất quá nhiều thời gian để AI suy nghĩ hoặc kết nối mạng không ổn định. Vui lòng thử lại."',
  MISSING_KEY: '⚙️ "Bạn chưa cấu hình Gemini API Key. Vui lòng vào phần Cài đặt cá nhân để thêm API Key trước khi sử dụng AI."',
  FILE_EXPIRED: '⚠️ "File đã hết hạn lưu trữ (quá 15 ngày). Vui lòng tải lên lại file mới để tiếp tục."',
  UNAUTHORIZED: '🔒 "Phiên làm việc đã hết hạn hoặc chưa đăng nhập. Vui lòng đăng nhập lại để tiếp tục."',
  UNKNOWN: '❌ "Đã có lỗi gián đoạn khi kết nối với trợ lý AI. Vui lòng tải lại trang và thử lại."'
};

/**
 * 6.2. Hàm tiện ích getFriendlyErrorMessage(rawError)
 * Tiếp nhận mọi kiểu dữ liệu lỗi (string, Error object, JSON response, HTTP status)
 * và ánh xạ chính xác theo Bảng quy chuẩn thông báo lỗi sang tiếng Việt.
 */
export const getFriendlyErrorMessage = (rawError: any): string => {
  if (rawError === undefined || rawError === null || rawError === '') {
    return FRIENDLY_ERROR_MAP.UNKNOWN;
  }

  // Nếu chuỗi đã được định dạng thân thiện sẵn, trả về nguyên bản
  if (typeof rawError === 'string') {
    const trimmed = rawError.trim();
    if (
      trimmed.startsWith('⚠️') ||
      trimmed.startsWith('⏳') ||
      trimmed.startsWith('🔑') ||
      trimmed.startsWith('🛡️') ||
      trimmed.startsWith('📶') ||
      trimmed.startsWith('⚙️') ||
      trimmed.startsWith('🔒') ||
      trimmed.startsWith('❌')
    ) {
      return trimmed;
    }
  }

  // Thu thập toàn bộ nội dung lỗi để regex/includes
  let text = '';
  if (typeof rawError === 'string') {
    text = rawError;
  } else if (typeof rawError === 'number') {
    text = String(rawError);
  } else if (typeof rawError === 'object') {
    const parts = [
      rawError.errorCode,
      rawError.code,
      rawError.status,
      rawError.statusCode,
      rawError.errorMessage,
      rawError.message,
      rawError.error,
      rawError.details,
      rawError.name
    ];
    try {
      if (typeof rawError.toString === 'function') {
        const str = rawError.toString();
        if (str && str !== '[object Object]') parts.push(str);
      }
    } catch {
      // ignore toString error
    }
    text = parts.filter(Boolean).join(' ');
  }

  const lower = text.toLowerCase();

  // 1. UNAUTHORIZED / SESSION
  if (
    lower.includes('unauthorized') ||
    lower.includes('401') ||
    lower.includes('chưa đăng nhập') ||
    lower.includes('phiên làm việc') ||
    lower.includes('phiên đăng nhập') ||
    lower.includes('token không hợp lệ') ||
    lower.includes('token hết hạn')
  ) {
    return FRIENDLY_ERROR_MAP.UNAUTHORIZED;
  }

  // 2. MISSING_KEY
  if (
    lower.includes('missing_key') ||
    lower.includes('missing key') ||
    lower.includes('chưa cấu hình') ||
    lower.includes('chưa có api key') ||
    lower.includes('chưa nhập api key') ||
    lower.includes('no api key')
  ) {
    return FRIENDLY_ERROR_MAP.MISSING_KEY;
  }

  // 3. API_KEY_INVALID, API key not valid, INVALID_KEY
  if (
    lower.includes('api_key_invalid') ||
    lower.includes('api key not valid') ||
    lower.includes('invalid_key') ||
    lower.includes('invalid api key') ||
    lower.includes('api key is invalid') ||
    lower.includes('key invalid') ||
    (lower.includes('invalid_argument') && lower.includes('key'))
  ) {
    return FRIENDLY_ERROR_MAP.API_KEY_INVALID;
  }

  // 4. quota, 429, RESOURCE_EXHAUSTED, hết hạn mức
  if (
    lower.includes('quota') ||
    lower.includes('429') ||
    lower.includes('resource_exhausted') ||
    lower.includes('hết hạn mức') ||
    lower.includes('rate_limit') ||
    lower.includes('rate limit') ||
    Boolean(rawError?.isQuotaExceeded)
  ) {
    return FRIENDLY_ERROR_MAP.QUOTA;
  }

  // 5. overloaded, 503, Service Unavailable, quá tải
  if (
    lower.includes('overloaded') ||
    lower.includes('503') ||
    lower.includes('service unavailable') ||
    lower.includes('quá tải') ||
    lower.includes('high demand')
  ) {
    return FRIENDLY_ERROR_MAP.OVERLOADED;
  }

  // 6. safety, block, vi phạm, Candidate was blocked
  if (
    lower.includes('safety') ||
    lower.includes('block') ||
    lower.includes('vi phạm') ||
    lower.includes('candidate was blocked') ||
    lower.includes('content policy') ||
    lower.includes('harm_category')
  ) {
    return FRIENDLY_ERROR_MAP.SAFETY;
  }

  // 7. timeout, DEADLINE_EXCEEDED, network
  if (
    lower.includes('timeout') ||
    lower.includes('deadline_exceeded') ||
    lower.includes('network') ||
    lower.includes('failed to fetch') ||
    lower.includes('connection') ||
    lower.includes('aborted')
  ) {
    return FRIENDLY_ERROR_MAP.TIMEOUT;
  }

  // 8. Hết hạn lưu trữ file (> 15 ngày)
  if (lower.includes('file_expired') || lower.includes('hết hạn lưu trữ') || lower.includes('quá 15 ngày')) {
    return FRIENDLY_ERROR_MAP.FILE_EXPIRED;
  }

  // 9. (Các lỗi không xác định khác)
  return FRIENDLY_ERROR_MAP.UNKNOWN;
};
